import React, { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface RichTextEditorProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  rows?: number;
}

/**
 * Rich text editor component for basic HTML formatting.
 * Supports: bold, lists (ul/ol), links.
 * Output is clean HTML that can be sent in emails.
 */
export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  label,
  value,
  onChange,
  placeholder,
  error,
  disabled,
  rows = 8,
}) => {
  // Configure toolbar with only basic formatting options
  const modules = useMemo(
    () => ({
      toolbar: [
        ['bold', 'italic'], // Basic text formatting
        [{ list: 'ordered' }, { list: 'bullet' }], // Lists
        ['link'], // Links
        ['clean'], // Remove formatting
      ],
    }),
    []
  );

  // Only allow basic formats
  const formats = ['bold', 'italic', 'list', 'bullet', 'link'];

  // Quill editor: toolbar is ~42px, so we need to account for it
  // Each row of content is approximately 24px
  const editorHeight = rows * 24;
  const containerHeight = editorHeight + 42; // Add toolbar height

  return (
    <div className="w-full">
      {label && (
        <label
          className="block text-neutral-700 mb-1.5"
          style={{
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
          }}
        >
          {label}
        </label>
      )}
      <div
        className={`
          quill
          ${error ? 'quill-error' : ''}
          ${disabled ? 'quill-disabled' : ''}
        `}
        style={{ height: `${containerHeight}px` }}
      >
        <ReactQuill
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={disabled}
          style={{
            height: '100%',
          }}
          theme="snow"
        />
      </div>
      {error && (
        <p
          className="mt-1.5 text-error-600"
          style={{
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default RichTextEditor;
